import { handleCreateTicketComment } from '@/entities/clinic';
import { fetchTicketComments } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import { generateFullName, showMessageError, showMessageSuccess } from '@/shared/lib';
import React, { useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import type { TicketComment, CommentBoxProps } from './commentBox';

export const MAX_CHARACTERS = 200;

/**
 * Drives the note thread of a study or support ticket comment box.
 * Fetches the ticket comments once the study is loaded, keeps the list scrolled to the newest note,
 * and handles drafting within MAX_CHARACTERS, pasting, Ctrl+Enter submit and posting new notes.
 *
 * @param props - Comment box props carrying studyId, ticketType and isLoadedData.
 * @returns The comments, loading flags, draft, refs, input handlers, author formatter and the
 * character-count flags.
 */
export const useCommentBox = ({
  studyId,
  ticketType = 'Study',
  isLoadedData = true,
}: Pick<CommentBoxProps, 'studyId' | 'ticketType' | 'isLoadedData'>) => {
  // State
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [newNote, setNewNote] = useState<string>('');
  const [loadingAddNote, setLoadingAddNote] = useState<boolean>(false);

  // Refs
  const notesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Redux
  /**
   * Current authenticated user record read from the user store. Selects the me slice so the comment box can mark comments written by the logged-in user.
   *
   * @returns The me object held in the user store.
   */
  const meRedux = useUserStore((s) => s.me);

  // Fetch comments
  /**
   * Loads the note thread attached to a study ticket. Requests the ticket comments oldest-first for the current study id and ticket type into state, logging and surfacing an error message on failure and clearing the loading flag either way.
   *
   * @returns A promise resolving once the comments and the loading flag have been stored in state.
   */
  const fetchComments = async () => {
    try {
      setLoading(true);
      const commentData = await fetchTicketComments(
        {
          ticketId: studyId,
          sortOrder: 'asc',
          ticketType,
        },
        0,
      );
      setComments((commentData || []) as TicketComment[]);
    } catch (error) {
      console.error('Failed to fetch ticket comments: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    } finally {
      setLoading(false);
    }
  };

  // Add new comment
  /**
   * Posts a new note onto a study ticket's note thread. Creates the comment with trimmed content and, on success, shows a success message and appends it to the local list; otherwise it surfaces an error message and throws so the caller stops.
   *
   * @param content - Note text to post.
   * @returns The created comment.
   */
  const addComment = async (content: string) => {
    try {
      const newComment = (await handleCreateTicketComment({
        ticketId: studyId,
        ticketType,
        content: content.trim(),
      })) as TicketComment | null;
      if (newComment) {
        showMessageSuccess({ content: t`Note added successfully.` });
        setComments((prevComments) => [...prevComments, newComment]);
        return newComment;
      }
      showMessageError({ content: 'Failed to add note. Please try again.' });
      throw new Error('Failed to add note. Please try again.');
    } catch (error) {
      console.error('Failed to create ticket comment: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      throw error;
    }
  };

  // Auto scroll to bottom
  /**
   * Keeps the newest study note in view. Sets the notes container's scroll position to its full scroll height when that container is mounted.
   *
   * @returns Nothing.
   */
  const scrollToBottom = () => {
    if (notesContainerRef.current) {
      notesContainerRef.current.scrollTop = notesContainerRef.current.scrollHeight;
    }
  };

  // Effects
  useEffect(() => {
    if (studyId && isLoadedData) {
      fetchComments();
    }
  }, [studyId, ticketType, isLoadedData]);

  useEffect(() => {
    scrollToBottom();
  }, [comments]);
  // Event handlers
  /**
   * Submits the drafted study note. Ignores an empty draft, raises the add-note loading flag while posting the trimmed text, clears the input once it succeeds and leaves error reporting to the create call.
   *
   * @returns A promise resolving once the note has been posted and the draft cleared.
   */
  const handleAddNote = async () => {
    const trimmedNote = newNote.trim();
    if (!trimmedNote) return;

    setLoadingAddNote(true);
    try {
      await addComment(trimmedNote);
      setNewNote('');
    } catch (_error) {
      // Error already handled in addComment
    } finally {
      setLoadingAddNote(false);
    }
  };

  /**
   * Tracks typing in the study note textarea. Copies the field value into the draft note state only while it stays within the MAX_CHARACTERS limit, so longer manual input is rejected.
   *
   * @param e - Change event from the note textarea.
   * @returns Nothing.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    // Giới hạn nhập thủ công ở 200 ký tự
    if (value.length <= MAX_CHARACTERS) {
      setNewNote(value);
    }
  };

  /**
   * Pastes clipboard text into the study note draft without exceeding the character limit. Cancels the native paste, splices the clipboard text over the current selection, truncates the result to MAX_CHARACTERS, then on the next tick restores the caret just after the portion that actually fit.
   *
   * @param e - Paste event from the note textarea.
   * @returns Nothing.
   */
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const textarea = e.currentTarget;
    const pastedText = e.clipboardData.getData('text');
    const currentValue = newNote;
    const { selectionStart } = textarea;
    const { selectionEnd } = textarea;

    // Nếu có text được chọn, thay thế phần đó; nếu không, chèn vào vị trí cursor
    const textBefore = currentValue.substring(0, selectionStart);
    const textAfter = currentValue.substring(selectionEnd);
    const newText = textBefore + pastedText + textAfter;

    // Giới hạn tổng độ dài ở 200 ký tự (cắt bớt nếu cần)
    const finalText = newText.substring(0, MAX_CHARACTERS);
    setNewNote(finalText);

    // Đặt lại vị trí cursor sau khi paste (dựa trên text thực tế sau khi cắt)
    setTimeout(() => {
      if (textareaRef.current) {
        // Tính toán vị trí cursor dựa trên độ dài text trước + độ dài text đã paste (sau khi cắt)
        const actualPastedLength = finalText.length - textBefore.length - textAfter.length;
        const newCursorPosition = Math.min(selectionStart + actualPastedLength, MAX_CHARACTERS);
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  /**
   * Provides the keyboard shortcut for posting a study note. Submits the current draft when Enter is pressed with Ctrl held.
   *
   * @param e - Key event from the note textarea.
   * @returns Nothing.
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleAddNote();
    }
  };

  // Helper functions
  /**
   * Labels the author of a study note in the comment list. Builds the creator's full name and appends '(You)' when the creator id matches the logged-in user held in Redux.
   *
   * @param comment - Comment whose creator is being labelled.
   * @returns Author name, suffixed for the current user.
   */
  const formatAuthorName = (comment: TicketComment) => {
    const authorName = generateFullName(comment.createdBy);
    const isCurrentUser = comment.createdBy.id === meRedux.id;
    return isCurrentUser ? `${authorName} (You)` : authorName;
  };

  // Computed values
  const characterCount = newNote.length;
  const isCharacterLimitReached = characterCount >= MAX_CHARACTERS;
  const isAddButtonDisabled = !newNote.trim() || characterCount > MAX_CHARACTERS || loadingAddNote;

  return {
    comments,
    loading,
    newNote,
    loadingAddNote,
    notesContainerRef,
    textareaRef,
    handleAddNote,
    handleInputChange,
    handlePaste,
    handleKeyPress,
    formatAuthorName,
    characterCount,
    isCharacterLimitReached,
    isAddButtonDisabled,
  };
};
